import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    quote: "Xpress Promotion has been amazing in helping us manage our growth across five locations. They take care of everything — postcards, design, coordinating with our local vendors — and it all looks like it's coming from one place. As we expand, they help us fill the schedule from day one.",
    name: "Natalie Michelson",
    role: "Founder, Dental Arts — 6-Practice Dental Group",
    videoId: "5X-nKb9kS4k",
  },
  {
    quote: "We've been working with Xpress Promotion for the last 3 to 4 years, and the results have been phenomenal. Our presence on the web and on social media is incredible. We've gained tons of leads and new patients. If you want results, go to Xpress Promotion — they are the go-to company.",
    name: "Dr. Rohit Patel",
    role: "Lead Dentist & Owner, Putnam Bright Smile — 3 Offices",
    videoId: "EP_sCX1JyxA",
  },
  {
    quote: "Every time I talk to you guys, there's always something new I learn about SEO, websites, and marketing. You're super real with me. What I love is that you don't make unrealistic promises. You build a strategy, knowing it's not one-size-fits-all. Shawn's experience in a dental office makes all the difference.",
    name: "Dr. Munther Salem",
    role: "Lead Dentist & Co-Owner, Ivanhoe Dental Group — 2 Offices",
    videoId: "1opTh9dqJEQ",
  },
];

const TestimonialSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);

  const t = testimonials[current];

  return (
    <section className="py-20 md:py-28 bg-secondary" ref={ref}>
      <div className="max-w-5xl mx-auto section-padding">
        <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-3 text-center">
          Hear It Straight From Our Clients
        </p>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight text-center mb-12">
          Client Video Testimonials
        </h2>

        <div className={`${visible ? "reveal-up" : "opacity-0"}`}>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Video */}
            <div className="aspect-video rounded-xl overflow-hidden shadow-lg bg-foreground/5">
              <iframe
                key={t.videoId}
                src={`https://www.youtube-nocookie.com/embed/${t.videoId}`}
                title={`${t.name} testimonial`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                loading="lazy"
              />
            </div>

            {/* Quote */}
            <div className="relative bg-card border border-border rounded-xl p-8 shadow-sm">
              <Quote className="w-8 h-8 text-accent/20 absolute top-5 left-5" />
              <div className="relative z-10">
                <blockquote className="text-base md:text-lg text-foreground font-medium leading-relaxed mb-6 pt-4">
                  "{t.quote}"
                </blockquote>
                <div>
                  <p className="font-bold text-foreground">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-200 active:scale-95"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === current ? "bg-accent w-6" : "bg-border hover:bg-muted-foreground/40"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-200 active:scale-95"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
